import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BarChart3, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface Poll {
  id: string;
  title: string;
  description: string | null;
  options: any;
  created_at: string;
  votes?: { option_index: number; user_id: string }[];
}

export const PollsAndSurveys = () => {
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPoll, setNewPoll] = useState({
    title: "",
    description: "",
    options: ["", ""]
  });

  useEffect(() => {
    loadPolls();
  }, []);

  const loadPolls = async () => {
    const { data: pollsData } = await supabase
      .from("polls")
      .select("*, votes:poll_votes(option_index, user_id)")
      .order("created_at", { ascending: false });

    setPolls(pollsData || []);
    setLoading(false);
  };

  const createPoll = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const validOptions = newPoll.options.filter(opt => opt.trim());
    if (validOptions.length < 2) {
      toast.error("Please add at least 2 options");
      return;
    }

    const { error } = await supabase
      .from("polls")
      .insert({
        title: newPoll.title,
        description: newPoll.description || null,
        options: validOptions.map(text => ({ text })),
        created_by: session.user.id
      });

    if (error) {
      toast.error("Failed to create poll");
    } else {
      toast.success("Poll created!");
      setDialogOpen(false);
      setNewPoll({ title: "", description: "", options: ["", ""] });
      loadPolls();
      
      // Track activity
      await supabase.from("activity_feed").insert({
        user_id: session.user.id,
        activity_type: "poll_created"
      });
    }
  };

  const vote = async (pollId: string, optionIndex: number) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast.error("Please sign in to vote");
      return;
    }

    const { error } = await supabase
      .from("poll_votes")
      .insert({
        poll_id: pollId,
        user_id: session.user.id,
        option_index: optionIndex
      });

    if (error) {
      if (error.code === "23505") {
        toast.error("You've already voted on this poll");
      } else {
        toast.error("Failed to vote");
      }
    } else {
      toast.success("Vote recorded!");
      loadPolls();
      
      // Track activity
      await supabase.from("activity_feed").insert({
        user_id: session.user.id,
        activity_type: "poll_voted",
        metadata: { poll_id: pollId }
      });
    }
  };

  const addOption = () => {
    setNewPoll({ ...newPoll, options: [...newPoll.options, ""] });
  };

  const removeOption = (index: number) => {
    setNewPoll({
      ...newPoll,
      options: newPoll.options.filter((_, i) => i !== index)
    });
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...newPoll.options];
    newOptions[index] = value;
    setNewPoll({ ...newPoll, options: newOptions });
  };

  if (loading) {
    return <div className="text-center py-8">Loading polls...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Polls & Surveys</h2>
          <p className="text-muted-foreground">Share your opinion and see what others think</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Poll
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Poll</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                placeholder="Poll Question"
                value={newPoll.title}
                onChange={(e) => setNewPoll({ ...newPoll, title: e.target.value })}
              />
              <Input
                placeholder="Description (optional)"
                value={newPoll.description}
                onChange={(e) => setNewPoll({ ...newPoll, description: e.target.value })}
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">Options</label>
                {newPoll.options.map((option, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder={`Option ${index + 1}`}
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                    />
                    {newPoll.options.length > 2 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeOption(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" onClick={addOption} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Option
                </Button>
              </div>
              <Button onClick={createPoll} className="w-full">Create Poll</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {polls.map((poll) => {
          const totalVotes = poll.votes?.length || 0;
          const [currentSession, setCurrentSession] = useState<any>(null);
          
          useEffect(() => {
            supabase.auth.getSession().then(({ data: { session } }) => {
              setCurrentSession(session);
            });
          }, []);
          
          const hasVoted = poll.votes?.some(v => v.user_id === currentSession?.user?.id);

          return (
            <Card key={poll.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      {poll.title}
                    </CardTitle>
                    {poll.description && (
                      <CardDescription className="mt-2">{poll.description}</CardDescription>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {poll.options.map((option, index) => {
                  const optionVotes = poll.votes?.filter(v => v.option_index === index).length || 0;
                  const percentage = totalVotes > 0 ? (optionVotes / totalVotes) * 100 : 0;

                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Button
                          variant={hasVoted ? "outline" : "secondary"}
                          className="flex-1 justify-start"
                          onClick={() => vote(poll.id, index)}
                          disabled={hasVoted}
                        >
                          {option.text}
                        </Button>
                        {hasVoted && (
                          <span className="ml-2 text-sm text-muted-foreground">
                            {optionVotes} ({percentage.toFixed(0)}%)
                          </span>
                        )}
                      </div>
                      {hasVoted && (
                        <Progress value={percentage} className="h-2" />
                      )}
                    </div>
                  );
                })}
                <p className="text-sm text-muted-foreground text-center mt-4">
                  {totalVotes} {totalVotes === 1 ? "vote" : "votes"}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {polls.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No Polls Yet</h3>
            <p className="text-muted-foreground">Be the first to create a poll!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};